import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Map, Users, ClipboardCheck, Mail, LogOut, Menu, X, BookOpen, Globe2,
  BarChart3, History, CalendarClock,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
  { to: "/parcels", label: "Assets", icon: Map, testid: "nav-parcels" },
  { to: "/map", label: "Map", icon: Globe2, testid: "nav-map" },
  { to: "/compliance", label: "Compliance", icon: ClipboardCheck, testid: "nav-compliance" },
  { to: "/renewals", label: "Renewals", icon: CalendarClock, testid: "nav-renewals" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics", adminOnly: true },
  { to: "/activity", label: "Activity", icon: History, testid: "nav-activity", adminOnly: true },
  { to: "/team", label: "Team", icon: Users, testid: "nav-team", adminOnly: true },
  { to: "/notifications", label: "Notifications", icon: Mail, testid: "nav-notifications", adminOnly: true },
];

export const Shell = ({ children }) => {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [account, setAccount] = useState(false);

  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const items = NAV.filter((n) => !n.adminOnly || user?.role === "admin");

  const doLogout = async () => {
    await logout();
    setOpen(false);
    setAccount(false);
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-background">
      <aside className="hidden md:flex md:w-60 lg:w-64 shrink-0 flex-col bg-zinc-950 text-white">
        <div className="px-6 py-7">
          <Logo size={32} tone="light" />
        </div>
        <nav className="flex-1 px-3 space-y-1">
          {items.map((n) => {
            const active = loc.pathname === n.to || (n.to !== "/" && loc.pathname.startsWith(n.to));
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
                }`}
              >
                <n.icon size={17} strokeWidth={2} />
                {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-5 border-t border-zinc-900">
          <a
            href={`${API}/manuals/${user?.role === "admin" ? "admin" : "executive"}?auth=${localStorage.getItem("token")}`}
            target="_blank" rel="noreferrer" data-testid="sidebar-manual-link"
            className="flex items-center gap-2 text-xs text-zinc-400 hover:text-primary transition-colors duration-200"
          >
            <BookOpen size={14} /> User manual (PDF)
          </a>
          <p className="mt-4 text-sm font-semibold truncate">{user?.name}</p>
          <p className="label-tiny mt-1 text-zinc-500">{user?.role}</p>
          <button
            data-testid="logout-button"
            onClick={doLogout}
            className="mt-4 flex items-center gap-2 text-xs text-zinc-500 hover:text-primary transition-colors duration-200"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <header className="md:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 bg-zinc-950 text-white">
        <Logo size={26} tone="light" />
        <div className="flex items-center gap-1">
          <button
            data-testid="mobile-account-toggle"
            onClick={() => { setAccount(!account); setOpen(false); }}
            className="flex items-center gap-2 rounded-full border border-zinc-800 pl-1 pr-2.5 py-1 transition-colors duration-200 hover:border-primary/50"
          >
            <span className="grid h-7 w-7 place-items-center rounded-full bg-primary text-[11px] font-bold">
              {initials}
            </span>
            <span className="text-[11px] font-semibold max-w-[90px] truncate">{user?.name}</span>
          </button>
          <Button
            variant="ghost"
            size="icon"
            data-testid="mobile-menu-toggle"
            onClick={() => { setOpen(!open); setAccount(false); }}
            className="text-white hover:bg-zinc-900 hover:text-white"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </Button>
        </div>
      </header>
      {account && (
        <div className="md:hidden bg-zinc-950 text-white px-4 pb-4" data-testid="mobile-account-panel">
          <div className="rounded-xl border border-zinc-800 p-4">
            <p className="text-sm font-semibold">{user?.name}</p>
            <p className="text-xs text-zinc-500">{user?.email}</p>
            <p className="label-tiny mt-1 text-zinc-500">{user?.role}</p>
            <button
              onClick={doLogout}
              data-testid="account-logout-button"
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground active:scale-[0.98] transition-transform duration-150"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      )}
      {open && (
        <div className="md:hidden bg-zinc-950 text-white px-4 pb-4 space-y-1">
          {items.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`m-${n.testid}`}
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-zinc-300 hover:bg-zinc-900"
            >
              <n.icon size={17} /> {n.label}
            </Link>
          ))}
          <button
            onClick={doLogout}
            data-testid="m-logout-button"
            className="flex items-center gap-3 px-3 py-3 text-sm text-primary w-full"
          >
            <LogOut size={17} /> Sign out
          </button>
          <a
            href={`${API}/manuals/${user?.role === "admin" ? "admin" : "executive"}?auth=${localStorage.getItem("token")}`}
            target="_blank" rel="noreferrer" data-testid="m-manual-link"
            className="flex items-center gap-3 px-3 py-3 text-sm text-zinc-300"
          >
            <BookOpen size={17} /> User manual (PDF)
          </a>
        </div>
      )}

      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
};
